import { useEffect, useMemo, useState } from "react";
import { ChevronDown, Save, Upload } from "lucide-react";
import { toast } from "sonner";
import { services } from "../data/services";
import { AZ, loadOverrides } from "../i18n";
import { storageMediaUrl } from "../lib/media";
import { SafeImage } from "./Media";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full rounded-lg border border-white/15 bg-surface px-3 py-2 text-sm text-white outline-none focus:border-gold";

async function putOverride(key, value) {
  const r = await fetch(`${API}/admin/content`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ key, value }),
  });
  if (!r.ok) throw new Error("Save failed");
}

async function uploadMedia(file) {
  const fd = new FormData();
  fd.append("file", file);
  const r = await fetch(`${API}/admin/media`, { method: "POST", credentials: "include", body: fd });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.detail || "Upload failed");
  return storageMediaUrl(data.url || (data.id ? `/api/media/${data.id}` : ""));
}

function serviceFields(service) {
  const raw = [
    ["Service name", service.name],
    ["Short / tagline", service.short],
    ["Overview", service.overview],
    ["Business problem", service.problem],
    ["Commercial impact", service.commercialImpact],
    ["Measures of success", service.measures],
    ...(service.subs || []).flatMap((x, i) => [[`Coverage ${i + 1} — title`, x.t], [`Coverage ${i + 1} — description`, x.d]]),
    ...(service.outcomes || []).map((x, i) => [`Outcome ${i + 1}`, x]),
    ...(service.deliverables || []).map((x, i) => [`Deliverable ${i + 1}`, x]),
    ...(service.steps || []).flatMap((x, i) => [[`Step ${i + 1} — title`, x.t], [`Step ${i + 1} — description`, x.d]]),
  ];
  const seen = new Set();
  return raw.filter(([, key]) => key && !seen.has(key) && seen.add(key));
}

function ServiceEditor({ service, overrides, onSaved }) {
  const fields = useMemo(() => serviceFields(service), [service]);
  const [values, setValues] = useState(() => Object.fromEntries(fields.map(([label, key]) => [key, {
    label,
    en: overrides[key]?.en || key,
    az: overrides[key]?.az || AZ[key] || "",
  }])));
  const heroKey = `image:service:${service.slug}`;
  const [hero, setHero] = useState(overrides[heroKey]?.en || overrides[heroKey]?.az || service.heroImg || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setValues(Object.fromEntries(fields.map(([label, key]) => [key, {
      label,
      en: overrides[key]?.en || key,
      az: overrides[key]?.az || AZ[key] || "",
    }])));
    setHero(overrides[heroKey]?.en || overrides[heroKey]?.az || service.heroImg || "");
  }, [overrides, fields, heroKey, service.heroImg]);

  const save = async () => {
    setBusy(true);
    try {
      await Promise.all([
        ...Object.entries(values).map(([key, v]) => putOverride(key, { en: v.en, az: v.az })),
        putOverride(heroKey, { en: storageMediaUrl(hero), az: storageMediaUrl(hero) }),
      ]);
      await loadOverrides(true);
      toast.success(`${service.name} saved.`);
      onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const uploadHero = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const url = await uploadMedia(file);
      setHero(url);
      await putOverride(heroKey, { en: url, az: url });
      await loadOverrides(true);
      onSaved();
      toast.success("Service image uploaded.");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <details className="border-t border-white/10 py-4">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-semibold text-white">
        <span>{service.index} · {service.name}</span>
        <ChevronDown className="h-4 w-4 text-white/45" />
      </summary>
      <div className="mt-5 space-y-5">
        <div>
          <p className="mb-2 font-mono text-[10px] uppercase tracking-[.16em] text-white/50">Hero / service image</p>
          <div className="grid gap-3 md:grid-cols-[220px_1fr] md:items-center">
            <div className="aspect-video overflow-hidden border border-white/10 bg-black/20"><SafeImage src={hero} alt="Service preview" className="h-full w-full object-cover" fallbackClassName="h-full w-full" /></div>
            <div className="space-y-2">
              <input value={hero} onChange={(e) => setHero(e.target.value)} className={inputCls} placeholder="Image URL" />
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-xs text-white hover:border-gold hover:text-gold">
                <Upload className="h-4 w-4" /> Upload image
                <input type="file" accept="image/*" className="sr-only" onChange={(e) => { uploadHero(e.target.files?.[0]); e.target.value = ""; }} />
              </label>
            </div>
          </div>
        </div>

        {Object.entries(values).map(([key, v]) => (
          <div key={key} className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-[11px] text-white/45">{v.label} · EN</label>
              <textarea rows={key.length > 90 ? 4 : 2} value={v.en} onChange={(e) => setValues((old) => ({ ...old, [key]: { ...old[key], en: e.target.value } }))} className={inputCls} />
            </div>
            <div>
              <label className="mb-1 block text-[11px] text-white/45">{v.label} · AZ</label>
              <textarea rows={key.length > 90 ? 4 : 2} value={v.az} onChange={(e) => setValues((old) => ({ ...old, [key]: { ...old[key], az: e.target.value } }))} className={inputCls} />
            </div>
          </div>
        ))}
        <button type="button" disabled={busy} onClick={save} className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-ink disabled:opacity-50">
          <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save service"}
        </button>
      </div>
    </details>
  );
}

export function ServicesAdmin() {
  const [overrides, setOverrides] = useState({});
  const reload = () => loadOverrides(true).then(setOverrides);
  useEffect(() => { reload(); }, []);

  return (
    <section data-testid="services-admin" className="mt-10 border border-white/10 bg-panel p-6 sm:p-8">
      <p className="font-mono text-xs uppercase tracking-[.3em] text-gold">Services</p>
      <h2 className="mt-3 text-2xl text-white">Xidmətlər bölməsinin idarəsi</h2>
      <p className="mt-2 max-w-3xl text-sm text-mist">Xidmət adı, açıqlamalar, problem, nəticələr, addımlar, deliverables və vizualı AZ/EN ayrıca dəyişdirilə bilər. Dizayn strukturu sabit qalır.</p>
      <div className="mt-6">
        {services.map((service) => <ServiceEditor key={service.slug} service={service} overrides={overrides} onSaved={reload} />)}
      </div>
    </section>
  );
}
